import React, { useState } from 'react';

import debounce from 'lodash/debounce';
import get from 'lodash/fp/get';
import isEmpty from 'lodash/isEmpty';

import { DEBOUNCE_DELAY } from '@lumapps/constants';
import { EventBlock } from '@lumapps/events/components/EventBlock';
import { EVENTS } from '@lumapps/events/keys';
import { Event } from '@lumapps/events/types';
import { instanceSelector } from '@lumapps/instance/ducks/instance-selector';
import { Sorters } from '@lumapps/lumx-filter-and-sort/components/Sorters';
import { useListFilter } from '@lumapps/lumx-filter-and-sort/hooks/useListFilter';
import { Alignment, ButtonProps, DialogProps, FlexBox } from '@lumapps/lumx/react';
import { PickerDialog } from '@lumapps/pickers/components/PickerDialog';
import { useSelector } from '@lumapps/redux/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { BaseLoadingStatus } from '@lumapps/utils/types/BaseLoadingStatus';

import { DEFAULT_FILTERS, SORT_FIELDS } from '../../constants';
import { actions, reducer, initialState } from '../../ducks/slice';
import { fetchEventList } from '../../ducks/thunks/fetchEventList';
import { fetchMoreEventList } from '../../ducks/thunks/fetchMoreEventList';
import { EVENTS_PICKER } from '../../keys';
import { EventPickerFilter, SortField } from '../../types';
import { EventPickerFilters } from '../EventPickerFilters';
import { EventBlockSkeletons } from './EventBlockSkeletons';

export interface EventPickerProps {
    /** props that will be passed in into the Confirm button */
    confirmButtonProps?: ButtonProps;
    /** whether the multiple selection is allowed */
    isMultipleSelect?: boolean;
    /** whether the picker is open or not */
    isOpen?: boolean;
    /** event already selected on picker init */
    initSelectedItems?: Event[];
    /** callback to be executed once the event selection is canceled */
    onCancel: () => void;
    /** callback to be executed once the event has been chosen */
    onConfirm: (selectedItems: Event[]) => void;
    /** callback when dialog visibility has changed */
    onVisibilityChange?: DialogProps['onVisibilityChange'];
    /** Filter events on current instance */
    onlyUseCurrentInstance?: boolean;
    /** variant to override the size of the article block */
    variant?: string;
}

export const CLASSNAME = 'event-picker';

/**
 * Reusable event picker, allowing the user to choose an event, searching by name and filter
 * events by communities and event start date / event date.
 *
 * @family Pickers
 * @param EventPickerProps
 * @returns EventPicker
 */
export const EventPicker: React.FC<EventPickerProps> = ({
    confirmButtonProps,
    isOpen,
    isMultipleSelect = true,
    initSelectedItems,
    onCancel,
    onConfirm,
    onVisibilityChange,
    onlyUseCurrentInstance = false,
    variant,
}) => {
    const { translateKey } = useTranslate();
    const { id: currentSiteId } = useSelector(instanceSelector);

    const [query, setQuery] = useState('');
    const [state, dispatchAction] = React.useReducer(reducer, initialState);
    const isListLoading = state.status === BaseLoadingStatus.loading;
    const isListLoadingMore = state.status === BaseLoadingStatus.loadingMore;
    const isListHasError = state.status === BaseLoadingStatus.error;

    const [sortField, setSortField] = useState(SORT_FIELDS[0]);
    const pickerFilter = useListFilter<EventPickerFilter>({
        defaultSelected: DEFAULT_FILTERS,
    });
    const siteWithoutEvents =
        isEmpty(state.items) &&
        state.status === BaseLoadingStatus.idle &&
        pickerFilter.selected === DEFAULT_FILTERS &&
        isEmpty(query);

    const onFetchPageDebounced = React.useMemo(() => {
        return debounce(fetchEventList(dispatchAction), DEBOUNCE_DELAY);
    }, []);

    const onFetchMoreDebounced = React.useMemo(() => {
        return debounce(fetchMoreEventList({ dispatchAction, state }), DEBOUNCE_DELAY);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.cursor]);

    const emptyStateLabel = React.useMemo(() => {
        if (siteWithoutEvents) {
            return translateKey(GLOBAL.NO_RESULTS);
        }
        return isEmpty(query) ? translateKey(EVENTS.FILTERS_NO_RESULTS) : undefined;
    }, [query, siteWithoutEvents, translateKey]);

    const onNoResultsClear = () => {
        pickerFilter.onResetFilters();
        setQuery('');
    };

    // Search on open or search value change or sort order change or filter change.
    React.useEffect(() => {
        if (isOpen) {
            dispatchAction(actions.setStatus(BaseLoadingStatus.loading));
            onFetchPageDebounced({
                searchValue: query,
                filters: pickerFilter.selected,
                sortField,
                ...(onlyUseCurrentInstance && { siteIds: [currentSiteId] }),
            });
        }
    }, [onFetchPageDebounced, isOpen, query, pickerFilter.selected, sortField, onlyUseCurrentInstance, currentSiteId]);

    const onSortFieldChange = (sort: SortField) => sort && setSortField(sort);

    const additionalFilterFields = (
        <>
            <EventPickerFilters
                pickerFilter={pickerFilter}
                isDisabled={state.status !== BaseLoadingStatus.idle || siteWithoutEvents}
            />
            <FlexBox marginAuto={Alignment.left}>
                <Sorters
                    isChangingOrderEnabled={false}
                    choices={SORT_FIELDS}
                    sort={sortField?.id}
                    onChange={(sort) => onSortFieldChange(SORT_FIELDS.find((s) => s.id === sort) as SortField)}
                    isDisabled={isListLoading || isListLoadingMore}
                    sortButtonProps={{
                        'aria-label': translateKey(GLOBAL.SORTING),
                    }}
                />
            </FlexBox>
        </>
    );

    const itemRenderer = React.useCallback(
        (event) => {
            return <EventBlock variant={variant} event={event} />;
        },
        [variant],
    );

    return (
        <PickerDialog
            isOpen={isOpen}
            isMultipleSelect={isMultipleSelect}
            resetListOnItemsChange={!isMultipleSelect}
            additionalFilterFields={additionalFilterFields}
            searchValue={query}
            onNoResultsClear={onNoResultsClear}
            emptyStateLabel={emptyStateLabel}
            shouldDisplayEmptyState={
                isEmpty(state.items) &&
                state.status === BaseLoadingStatus.idle &&
                pickerFilter.selected === DEFAULT_FILTERS &&
                isEmpty(query)
            }
            className={CLASSNAME}
            onSearchChange={setQuery}
            title={translateKey(EVENTS_PICKER.TITLE)}
            searchPlaceholder={translateKey(GLOBAL.SEARCH_BY_TITLE)}
            itemRenderer={itemRenderer}
            itemKeySelector={get('id')}
            onScrollEnd={onFetchMoreDebounced}
            isListLoading={isListLoading}
            items={isListLoading ? [] : state.items}
            initSelectedItems={initSelectedItems}
            afterList={(isListLoading || isListLoadingMore) && EventBlockSkeletons}
            confirmButtonProps={confirmButtonProps || { children: translateKey(GLOBAL.SELECT) }}
            shouldPreventClose={false}
            onSelectionConfirm={onConfirm}
            selectedItemsLabelKey={EVENTS.SELECTED_EVENTS_COUNT}
            onSelectionCancel={onCancel}
            hasError={isListHasError}
            onVisibilityChange={(isVisible) => {
                if (!isVisible) {
                    dispatchAction(actions.reset());

                    if (onVisibilityChange) {
                        onVisibilityChange(isVisible);
                    }
                }
            }}
        />
    );
};
